import React, { useEffect, useState } from "react";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { useCrm } from "../../pages/crm/CrmApp";
import { money } from "../../lib/crmApi";
import { inputCls, Labeled } from "../market/admin/ui";

const EMPTY = { name: "", email: "", password: "", phone: "", commission_type: "percent", commission_value: 5, active: true };

const UserDialog = ({ open, initial, onClose, onSaved }) => {
  const { api } = useCrm();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (open) {
      setF({ ...EMPTY, ...(initial || {}), password: "" });
      setErr("");
    }
  }, [open, initial]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api(f.id ? `/team/${f.id}` : "/team", { method: f.id ? "PUT" : "POST", body: { ...f, commission_value: Number(f.commission_value) || 0, password: f.password || null } });
      onSaved();
      onClose();
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="crm-user-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Editar integrante" : "Nuevo vendedor"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Labeled label="Nombre"><input required className={inputCls} value={f.name} onChange={set("name")} data-testid="crm-user-name" /></Labeled>
          <Labeled label="Correo (para entrar)"><input required type="email" className={inputCls} value={f.email} onChange={set("email")} data-testid="crm-user-email" /></Labeled>
          <Labeled label={f.id ? "Nueva contraseña (opcional)" : "Contraseña"}><input required={!f.id} minLength={6} className={inputCls} value={f.password} onChange={set("password")} data-testid="crm-user-password" /></Labeled>
          <Labeled label="WhatsApp"><input className={inputCls} value={f.phone} onChange={set("phone")} data-testid="crm-user-phone" /></Labeled>
          {f.role !== "owner" && (
            <div className="grid grid-cols-2 gap-3">
              <Labeled label="Comisión">
                <select className={inputCls} value={f.commission_type} onChange={set("commission_type")} data-testid="crm-user-commission-type">
                  <option value="percent">Porcentaje %</option>
                  <option value="fixed">Monto fijo $</option>
                </select>
              </Labeled>
              <Labeled label={f.commission_type === "percent" ? "% por venta" : "$ por venta"}><input type="number" min="0" step="any" className={inputCls} value={f.commission_value} onChange={set("commission_value")} data-testid="crm-user-commission-value" /></Labeled>
            </div>
          )}
          {f.id && f.role !== "owner" && (
            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3.5 py-2.5">
              <span className="text-sm text-slate-700">Puede entrar</span>
              <Switch checked={f.active} onCheckedChange={(v) => setF((x) => ({ ...x, active: v }))} data-testid="crm-user-active" />
            </div>
          )}
          {err && <p className="text-sm text-red-600" data-testid="crm-user-error">{err}</p>}
          <button disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold text-white" style={{ background: "var(--crm)" }} data-testid="crm-user-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const Team = () => {
  const { api } = useCrm();
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api("/team").then(setList);
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const max = Math.max(1, ...(list || []).map((u) => u.stats.sales));
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Vendedores</h1>
        <button onClick={() => setEdit({})} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold text-white shadow" style={{ background: "var(--crm)" }} data-testid="crm-add-user"><Plus className="h-4 w-4" /> Vendedor</button>
      </div>
      <p className="text-sm text-slate-500">Cada vendedor solo ve sus propios clientes, cotizaciones y ventas.</p>
      <div className="space-y-2">
        {(list || []).map((u, i) => (
          <div key={u.id} className={`rounded-2xl border border-slate-200 bg-white p-4 ${u.active ? "" : "opacity-50"}`} data-testid={`crm-user-${u.id}`}>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 font-bold text-slate-700">{u.name[0]}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-900">{u.name} {u.role === "owner" && <span className="text-[10px] font-normal text-slate-400">· Administrador</span>}</p>
                <p className="truncate text-xs text-slate-500">{u.email} · {u.role === "owner" ? "sin comisión" : u.commission_type === "percent" ? `${u.commission_value}% por venta` : `${money(u.commission_value)} por venta`}</p>
              </div>
              <button onClick={() => setEdit(u)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" data-testid={`crm-user-edit-${u.id}`}><Pencil className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full" style={{ width: `${(u.stats.sales / max) * 100}%`, background: "var(--crm)" }} /></div>
            <div className="mt-2 grid grid-cols-4 gap-2 text-center text-[11px] text-slate-500">
              <span><b className="block text-sm text-slate-900">{u.contacts}</b>clientes</span>
              <span><b className="block text-sm text-slate-900">{u.stats.count}</b>ventas</span>
              <span><b className="block text-sm text-slate-900">{money(u.stats.sales)}</b>vendido</span>
              <span><b className="block text-sm text-amber-600" data-testid={`crm-user-pending-${u.id}`}>{money(u.stats.commission_pending)}</b>por pagar</span>
            </div>
            {i === 0 && null}
          </div>
        ))}
      </div>
      <UserDialog open={!!edit} initial={edit} onClose={() => setEdit(null)} onSaved={load} />
    </div>
  );
};
